/**
 * Year 11 ICT - Unit Tests, Exercises, and Resources
 * Complete assessment materials for all 15 units
 * Based on Vanuatu National ICT Curriculum - Year 11
 */

// ============================================
// TEST BLUEPRINTS FOR ALL UNITS
// ============================================
export const year11ICTTestBlueprints = {
  // TERM 1
  unit1: {
    unitTitle: "Unit 1: Introduction to Computer Systems",
    strand: "Strand 1 - Personal Computer Systems",
    subStrand: "1.1",
    duration: "45 minutes",
    totalMarks: 40,
    slosCovered: ["Hardware components", "Operating system basics", "GUI", "Input/output devices", "Storage devices"],
    breakdown: {
      multipleChoice: { count: 15, marks: 15, percentage: 37.5 },
      shortAnswer: { count: 5, marks: 15, percentage: 37.5 },
      extendedResponse: { count: 1, marks: 10, percentage: 25 }
    },
    cognitiveLevel: {
      knowledge: "40%",
      comprehension: "30%",
      application: "20%",
      analysis: "10%"
    }
  },
  unit2: {
    unitTitle: "Unit 2: History of Computers",
    strand: "Strand 1 - Personal Computer Systems",
    subStrand: "1.2",
    duration: "30 minutes",
    totalMarks: 25,
    slosCovered: ["Early computer inventors", "Evolution of computing", "Development of the Internet"],
    breakdown: {
      multipleChoice: { count: 10, marks: 10, percentage: 40 },
      shortAnswer: { count: 3, marks: 9, percentage: 36 },
      extendedResponse: { count: 1, marks: 6, percentage: 24 }
    },
    cognitiveLevel: {
      knowledge: "50%",
      comprehension: "30%",
      application: "15%",
      analysis: "5%"
    }
  },
  unit3: {
    unitTitle: "Unit 3: File Management & Security",
    strand: "Strand 1 - Personal Computer Systems",
    subStrand: "1.1",
    duration: "40 minutes",
    totalMarks: 35,
    slosCovered: ["File management", "Data privacy", "Data security", "Legal implications", "Protection measures"],
    breakdown: {
      multipleChoice: { count: 12, marks: 12, percentage: 34 },
      shortAnswer: { count: 4, marks: 12, percentage: 34 },
      extendedResponse: { count: 1, marks: 11, percentage: 32 }
    },
    cognitiveLevel: {
      knowledge: "35%",
      comprehension: "35%",
      application: "20%",
      analysis: "10%"
    }
  },
  unit4: {
    unitTitle: "Unit 4: Word Processing I",
    strand: "Strand 2 - Application Software",
    subStrand: "2.1",
    duration: "45 minutes",
    totalMarks: 40,
    slosCovered: ["Word processing basics", "Document creation", "Formatting", "Editing", "Printing"],
    breakdown: {
      multipleChoice: { count: 10, marks: 10, percentage: 25 },
      shortAnswer: { count: 4, marks: 12, percentage: 30 },
      practical: { count: 1, marks: 18, percentage: 45 }
    },
    cognitiveLevel: {
      knowledge: "25%",
      comprehension: "30%",
      application: "40%",
      analysis: "5%"
    }
  },
  // TERM 2
  unit5: {
    unitTitle: "Unit 5: Spreadsheets I",
    strand: "Strand 2 - Application Software",
    subStrand: "2.2",
    duration: "45 minutes",
    totalMarks: 40,
    slosCovered: ["Spreadsheet basics", "Formulas", "Functions", "Charts", "Data manipulation"],
    breakdown: {
      multipleChoice: { count: 10, marks: 10, percentage: 25 },
      shortAnswer: { count: 4, marks: 12, percentage: 30 },
      practical: { count: 1, marks: 18, percentage: 45 }
    },
    cognitiveLevel: {
      knowledge: "25%",
      comprehension: "25%",
      application: "40%",
      analysis: "10%"
    }
  },
  unit6: {
    unitTitle: "Unit 6: Databases I",
    strand: "Strand 2 - Application Software",
    subStrand: "2.3",
    duration: "45 minutes",
    totalMarks: 40,
    slosCovered: ["Database concepts", "Tables", "Data types", "Queries", "Reports"],
    breakdown: {
      multipleChoice: { count: 12, marks: 12, percentage: 30 },
      shortAnswer: { count: 5, marks: 15, percentage: 37.5 },
      extendedResponse: { count: 1, marks: 13, percentage: 32.5 }
    },
    cognitiveLevel: {
      knowledge: "30%",
      comprehension: "35%",
      application: "25%",
      analysis: "10%"
    }
  },
  // TERM 3
  unit7: {
    unitTitle: "Unit 7: Programming I",
    strand: "Strand 1 - Personal Computer Systems",
    subStrand: "1.3",
    duration: "45 minutes",
    totalMarks: 40,
    slosCovered: ["Problem solving", "Pseudocode", "Algorithms", "Flow diagrams", "Variables", "Control structures"],
    breakdown: {
      multipleChoice: { count: 10, marks: 10, percentage: 25 },
      shortAnswer: { count: 4, marks: 12, percentage: 30 },
      codingTask: { count: 2, marks: 18, percentage: 45 }
    },
    cognitiveLevel: {
      knowledge: "25%",
      comprehension: "25%",
      application: "40%",
      analysis: "10%"
    }
  },
  unit8: {
    unitTitle: "Unit 8: Keyboard Skills",
    strand: "Strand 1 - Personal Computer Systems",
    subStrand: "1.4",
    duration: "30 minutes",
    totalMarks: 30,
    slosCovered: ["Finger placement", "Touch typing", "Speed and accuracy"],
    breakdown: {
      multipleChoice: { count: 10, marks: 10, percentage: 33 },
      practical: { count: 1, marks: 20, percentage: 67 }
    },
    cognitiveLevel: {
      knowledge: "20%",
      comprehension: "20%",
      application: "60%",
      analysis: "0%"
    }
  },
  unit9: {
    unitTitle: "Unit 9: Desktop Publishing",
    strand: "Strand 3 - Graphics Manipulation",
    subStrand: "3.1",
    duration: "45 minutes",
    totalMarks: 40,
    slosCovered: ["DTP concepts", "Layout design", "Document elements", "Professional documents"],
    breakdown: {
      multipleChoice: { count: 10, marks: 10, percentage: 25 },
      shortAnswer: { count: 3, marks: 12, percentage: 30 },
      practical: { count: 1, marks: 18, percentage: 45 }
    },
    cognitiveLevel: {
      knowledge: "25%",
      comprehension: "25%",
      application: "45%",
      analysis: "5%"
    }
  },
  unit10: {
    unitTitle: "Unit 10: Computer Graphics I",
    strand: "Strand 3 - Graphics Manipulation",
    subStrand: "3.2",
    duration: "45 minutes",
    totalMarks: 35,
    slosCovered: ["Graphic design basics", "Design tools", "Elements of design"],
    breakdown: {
      multipleChoice: { count: 10, marks: 10, percentage: 28.5 },
      shortAnswer: { count: 3, marks: 10, percentage: 28.5 },
      practical: { count: 1, marks: 15, percentage: 43 }
    },
    cognitiveLevel: {
      knowledge: "30%",
      comprehension: "25%",
      application: "40%",
      analysis: "5%"
    }
  },
  // TERM 4
  unit11: {
    unitTitle: "Unit 11: Digital Image Processing",
    strand: "Strand 3 - Graphics Manipulation",
    subStrand: "3.3",
    duration: "40 minutes",
    totalMarks: 35,
    slosCovered: ["Image processing concepts", "Image modification", "Advanced features"],
    breakdown: {
      multipleChoice: { count: 10, marks: 10, percentage: 28.5 },
      shortAnswer: { count: 3, marks: 10, percentage: 28.5 },
      practical: { count: 1, marks: 15, percentage: 43 }
    },
    cognitiveLevel: {
      knowledge: "30%",
      comprehension: "30%",
      application: "35%",
      analysis: "5%"
    }
  },
  unit12: {
    unitTitle: "Unit 12: Presentation Software",
    strand: "Strand 3 - Graphics Manipulation",
    subStrand: "3.4",
    duration: "45 minutes",
    totalMarks: 40,
    slosCovered: ["Presentation planning", "Slide creation", "Animation", "Transitions", "Delivery"],
    breakdown: {
      multipleChoice: { count: 10, marks: 10, percentage: 25 },
      shortAnswer: { count: 3, marks: 12, percentage: 30 },
      practical: { count: 1, marks: 18, percentage: 45 }
    },
    cognitiveLevel: {
      knowledge: "25%",
      comprehension: "25%",
      application: "45%",
      analysis: "5%"
    }
  },
  unit13: {
    unitTitle: "Unit 13: Networks I",
    strand: "Strand 4 - Communication and Network Systems",
    subStrand: "4.1",
    duration: "40 minutes",
    totalMarks: 35,
    slosCovered: ["Network protocols", "Network types", "Network topology", "Network devices"],
    breakdown: {
      multipleChoice: { count: 12, marks: 12, percentage: 34 },
      shortAnswer: { count: 4, marks: 12, percentage: 34 },
      extendedResponse: { count: 1, marks: 11, percentage: 32 }
    },
    cognitiveLevel: {
      knowledge: "40%",
      comprehension: "35%",
      application: "20%",
      analysis: "5%"
    }
  },
  unit14: {
    unitTitle: "Unit 14: Web & Internet",
    strand: "Strand 4 - Communication and Network Systems",
    subStrand: "4.2, 4.3",
    duration: "45 minutes",
    totalMarks: 40,
    slosCovered: ["HTML basics", "Web pages", "Web browsers", "Search engines", "WWW structure"],
    breakdown: {
      multipleChoice: { count: 10, marks: 10, percentage: 25 },
      shortAnswer: { count: 4, marks: 12, percentage: 30 },
      practical: { count: 1, marks: 18, percentage: 45 }
    },
    cognitiveLevel: {
      knowledge: "25%",
      comprehension: "30%",
      application: "40%",
      analysis: "5%"
    }
  },
  unit15: {
    unitTitle: "Unit 15: Email Communication",
    strand: "Strand 4 - Communication and Network Systems",
    subStrand: "4.4",
    duration: "35 minutes",
    totalMarks: 30,
    slosCovered: ["Email concepts", "Email creation", "Attachments", "Email security", "SPAM protection"],
    breakdown: {
      multipleChoice: { count: 10, marks: 10, percentage: 33 },
      shortAnswer: { count: 4, marks: 12, percentage: 40 },
      extendedResponse: { count: 1, marks: 8, percentage: 27 }
    },
    cognitiveLevel: {
      knowledge: "35%",
      comprehension: "35%",
      application: "25%",
      analysis: "5%"
    }
  }
};

// ============================================
// UNIT TESTS WITH ANSWER KEYS
// ============================================
export const year11ICTUnitTests = {
  // ==========================================
  // UNIT 1: Introduction to Computer Systems
  // ==========================================
  unit1: {
    id: "y11ict-unit1-test",
    title: "Unit 1 Test: Introduction to Computer Systems",
    duration: "45 minutes",
    totalMarks: 40,
    instructions: [
      "Answer ALL questions",
      "Write your answers clearly",
      "Multiple choice: Circle the correct answer"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 15,
        questions: [
          { id: 1, question: "Which of the following is an input device?", options: ["A. Monitor", "B. Printer", "C. Keyboard", "D. Speaker"], answer: "C", marks: 1 },
          { id: 2, question: "The main circuit board inside a computer is called the:", options: ["A. CPU", "B. RAM", "C. Motherboard", "D. Hard drive"], answer: "C", marks: 1 },
          { id: 3, question: "Which component is known as the 'brain' of the computer?", options: ["A. RAM", "B. CPU", "C. Hard Drive", "D. Power Supply"], answer: "B", marks: 1 },
          { id: 4, question: "GUI stands for:", options: ["A. Graphical User Interface", "B. General User Input", "C. Graphic Universal Interface", "D. General Utility Interface"], answer: "A", marks: 1 },
          { id: 5, question: "Which is an example of secondary storage?", options: ["A. RAM", "B. Cache", "C. Hard Disk Drive", "D. Register"], answer: "C", marks: 1 },
          { id: 6, question: "The operating system is an example of:", options: ["A. Hardware", "B. System software", "C. Application software", "D. Firmware"], answer: "B", marks: 1 },
          { id: 7, question: "Which device is used to point and click on screen items?", options: ["A. Keyboard", "B. Monitor", "C. Mouse", "D. Printer"], answer: "C", marks: 1 },
          { id: 8, question: "RAM stands for:", options: ["A. Read Access Memory", "B. Random Access Memory", "C. Run Application Memory", "D. Rapid Access Module"], answer: "B", marks: 1 },
          { id: 9, question: "An inkjet printer is an example of:", options: ["A. Input device", "B. Output device", "C. Storage device", "D. Processing device"], answer: "B", marks: 1 },
          { id: 10, question: "Which key is used to type capital letters temporarily?", options: ["A. Caps Lock", "B. Tab", "C. Shift", "D. Enter"], answer: "C", marks: 1 },
          { id: 11, question: "Files and folders are managed using:", options: ["A. Word processor", "B. File Explorer/Manager", "C. Web browser", "D. Calculator"], answer: "B", marks: 1 },
          { id: 12, question: "Which of the following is volatile memory?", options: ["A. Hard Drive", "B. SSD", "C. RAM", "D. DVD"], answer: "C", marks: 1 },
          { id: 13, question: "A flash drive is also known as a:", options: ["A. Hard disk", "B. USB drive", "C. Floppy disk", "D. CD-ROM"], answer: "B", marks: 1 },
          { id: 14, question: "Which icon represents a folder on the desktop?", options: ["A. A file with lines", "B. A yellow rectangle", "C. A disk", "D. A question mark"], answer: "B", marks: 1 },
          { id: 15, question: "Starting a computer is called:", options: ["A. Loading", "B. Shutting down", "C. Booting", "D. Logging"], answer: "C", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 15,
        questions: [
          { id: 16, question: "List FIVE hardware components of a computer system.", marks: 5, answer: "CPU, Monitor, Keyboard, Mouse, RAM, Hard Drive, Motherboard, Power Supply, Speakers, Printer (any 5)" },
          { id: 17, question: "Explain TWO differences between RAM and Hard Drive storage.", marks: 4, answer: "RAM is temporary/volatile (loses data when power off) while Hard Drive is permanent/non-volatile. RAM is faster but has less capacity; Hard Drive is slower but stores more data." },
          { id: 18, question: "What are THREE main features of a Graphical User Interface (GUI)?", marks: 3, answer: "Icons, Windows, Menus, Pointer/Mouse cursor, Buttons, Desktop background (any 3)" },
          { id: 19, question: "Name THREE types of printers.", marks: 3, answer: "Inkjet printer, Laser printer, Dot matrix printer, 3D printer, Thermal printer (any 3)" }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 10,
        questions: [
          { id: 20, question: "A new student has never used a computer before. Explain the steps they would follow to: (a) Turn on the computer and log in to the operating system (b) Open a file from a USB flash drive. Include safety considerations.", marks: 10, answer: "Steps to turn on and log in: 1. Press power button, 2. Wait for OS to load, 3. Enter username/password if required, 4. Wait for desktop to appear. Opening USB file: 1. Insert USB into USB port, 2. Open File Explorer, 3. Click on USB drive letter, 4. Navigate to file location, 5. Double-click to open file. Safety: Don't force USB in wrong way, safely eject USB before removing, don't share password with others." }
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 2: History of Computers
  // ==========================================
  unit2: {
    id: "y11ict-unit2-test",
    title: "Unit 2 Test: History of Computers",
    duration: "30 minutes",
    totalMarks: 25,
    instructions: [
      "Answer ALL questions",
      "Write your answers clearly"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 10,
        questions: [
          { id: 1, question: "Charles Babbage is known as the:", options: ["A. Father of the Internet", "B. Father of the Computer", "C. Inventor of the Mouse", "D. Creator of Windows"], answer: "B", marks: 1 },
          { id: 2, question: "The first programmable computer was:", options: ["A. ENIAC", "B. Abacus", "C. Analytical Engine", "D. Apple II"], answer: "C", marks: 1 },
          { id: 3, question: "ENIAC stands for:", options: ["A. Electronic Numerical Integrator and Computer", "B. Electronic Network Interface and Computer", "C. Enhanced Number Input and Calculator", "D. Electronic New Input and Computer"], answer: "A", marks: 1 },
          { id: 4, question: "Ada Lovelace is credited as the first:", options: ["A. Computer hardware inventor", "B. Computer programmer", "C. Internet user", "D. Operating system developer"], answer: "B", marks: 1 },
          { id: 5, question: "The World Wide Web was invented by:", options: ["A. Bill Gates", "B. Steve Jobs", "C. Tim Berners-Lee", "D. Mark Zuckerberg"], answer: "C", marks: 1 },
          { id: 6, question: "First generation computers used:", options: ["A. Transistors", "B. Integrated circuits", "C. Vacuum tubes", "D. Microprocessors"], answer: "C", marks: 1 },
          { id: 7, question: "The abacus is an example of:", options: ["A. Modern computer", "B. Early calculating device", "C. Printer", "D. Storage device"], answer: "B", marks: 1 },
          { id: 8, question: "Second generation computers were characterized by:", options: ["A. Vacuum tubes", "B. Transistors", "C. Microprocessors", "D. Quantum chips"], answer: "B", marks: 1 },
          { id: 9, question: "The Apple II and IBM PC represent which generation?", options: ["A. First generation", "B. Second generation", "C. Third generation", "D. Fourth generation"], answer: "D", marks: 1 },
          { id: 10, question: "ARPANET was the precursor to:", options: ["A. Microsoft Windows", "B. The Internet", "C. Apple computers", "D. USB drives"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 9,
        questions: [
          { id: 11, question: "Name THREE important figures in computer history and their contributions.", marks: 3, answer: "Charles Babbage - Analytical Engine/Father of Computer; Ada Lovelace - First programmer; Alan Turing - Turing Machine/Computer science foundations; Tim Berners-Lee - World Wide Web (any 3)" },
          { id: 12, question: "Describe how computers have changed in size from first to fourth generation.", marks: 3, answer: "First generation: Room-sized machines using vacuum tubes. Second: Smaller due to transistors. Third: Desk-sized with integrated circuits. Fourth: Personal computers, laptops, smartphones using microprocessors - small enough to carry." },
          { id: 13, question: "What was the purpose of ARPANET and how did it lead to the modern Internet?", marks: 3, answer: "ARPANET was a military/research network connecting universities and research centers. It developed packet switching technology and protocols that became the foundation for the modern Internet." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 6,
        questions: [
          { id: 14, question: "Explain how the development of the Internet has changed the way people communicate and access information.", marks: 6, answer: "The Internet has revolutionized communication through: email (instant written messages), social media (connecting globally), video calls (face-to-face remotely). Information access changed through: search engines (find anything quickly), online libraries/databases, news websites, educational resources. Both personal and business communication transformed - faster, cheaper, more accessible worldwide." }
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 3: File Management & Security
  // ==========================================
  unit3: {
    id: "y11ict-unit3-test",
    title: "Unit 3 Test: File Management & Security",
    duration: "40 minutes",
    totalMarks: 35,
    instructions: [
      "Answer ALL questions",
      "Write your answers clearly"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "A file extension tells us:", options: ["A. The file size", "B. The file type", "C. The file location", "D. The file owner"], answer: "B", marks: 1 },
          { id: 2, question: "Which file extension indicates a Word document?", options: ["A. .xlsx", "B. .pptx", "C. .docx", "D. .pdf"], answer: "C", marks: 1 },
          { id: 3, question: "To organize files, you should put them in:", options: ["A. The recycle bin", "B. Folders", "C. The task bar", "D. The desktop only"], answer: "B", marks: 1 },
          { id: 4, question: "A computer virus is:", options: ["A. Helpful software", "B. Malicious software that spreads", "C. Operating system", "D. Hardware component"], answer: "B", marks: 1 },
          { id: 5, question: "Which is the BEST password?", options: ["A. 123456", "B. password", "C. MyD0g$Nam3!", "D. abc"], answer: "C", marks: 1 },
          { id: 6, question: "Backing up files means:", options: ["A. Deleting them", "B. Making copies for safety", "C. Renaming them", "D. Moving them to desktop"], answer: "B", marks: 1 },
          { id: 7, question: "Antivirus software is used to:", options: ["A. Create documents", "B. Protect against malware", "C. Browse the internet", "D. Edit photos"], answer: "B", marks: 1 },
          { id: 8, question: "Data privacy means:", options: ["A. Sharing all data publicly", "B. Controlling who accesses personal information", "C. Deleting all files", "D. Using only one computer"], answer: "B", marks: 1 },
          { id: 9, question: "The Recycle Bin stores:", options: ["A. System files", "B. Deleted files temporarily", "C. Software updates", "D. Internet history"], answer: "B", marks: 1 },
          { id: 10, question: "Which action helps protect data?", options: ["A. Sharing passwords", "B. Regular backups", "C. Opening email attachments from strangers", "D. Using the same password everywhere"], answer: "B", marks: 1 },
          { id: 11, question: "Copyright law protects:", options: ["A. Hardware from damage", "B. Creative works from being copied without permission", "C. Viruses from spreading", "D. Files from corruption"], answer: "B", marks: 1 },
          { id: 12, question: "Phishing is:", options: ["A. A computer game", "B. Tricking people into revealing personal information", "C. A type of printer", "D. A programming language"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 12,
        questions: [
          { id: 13, question: "List FOUR good practices for organizing files on a computer.", marks: 4, answer: "Use meaningful file names, Create folders for categories, Use consistent naming conventions, Delete unnecessary files regularly, Keep related files together" },
          { id: 14, question: "Explain THREE ways a computer's data can become corrupted or lost.", marks: 3, answer: "Virus/malware infection, Hardware failure (hard drive crash), Accidental deletion, Power failure during saving, Physical damage to storage device" },
          { id: 15, question: "What is the difference between data privacy and data security?", marks: 3, answer: "Data privacy: the right to control how personal information is collected, used, and shared. Data security: technical measures to protect data from unauthorized access, theft, or damage (firewalls, encryption, passwords)." },
          { id: 16, question: "Name TWO legal implications of unauthorized data access.", marks: 2, answer: "Criminal prosecution/fines, Civil lawsuits, Job termination, Prison sentences for serious offenses (any 2)" }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 11,
        questions: [
          { id: 17, question: "Your school is creating guidelines for computer lab users. Write FIVE important rules about data security and file management that should be included, and explain why each rule is important.", marks: 11, answer: "1. Use strong, unique passwords - prevents unauthorized access. 2. Log out when finished - protects account from misuse. 3. Don't install unauthorized software - prevents malware. 4. Save work regularly and backup important files - prevents data loss. 5. Don't share passwords - maintains individual accountability. 6. Report suspicious activity - helps maintain security for all users." }
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 4: Word Processing I
  // ==========================================
  unit4: {
    id: "y11ict-unit4-test",
    title: "Unit 4 Test: Word Processing I",
    duration: "45 minutes",
    totalMarks: 40,
    instructions: [
      "Answer ALL questions",
      "Section C requires practical demonstration"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 10,
        questions: [
          { id: 1, question: "Which software is used for word processing?", options: ["A. Excel", "B. Word", "C. PowerPoint", "D. Access"], answer: "B", marks: 1 },
          { id: 2, question: "To make text BOLD, you press:", options: ["A. Ctrl + I", "B. Ctrl + U", "C. Ctrl + B", "D. Ctrl + S"], answer: "C", marks: 1 },
          { id: 3, question: "Ctrl + S is used to:", options: ["A. Select all", "B. Save document", "C. Search text", "D. Strikethrough"], answer: "B", marks: 1 },
          { id: 4, question: "The blinking line that shows where you'll type is called:", options: ["A. Mouse pointer", "B. Cursor", "C. Icon", "D. Tab"], answer: "B", marks: 1 },
          { id: 5, question: "To center text, you use:", options: ["A. Left align", "B. Right align", "C. Center align", "D. Justify"], answer: "C", marks: 1 },
          { id: 6, question: "Font refers to:", options: ["A. Text size only", "B. Text style/typeface", "C. Text color only", "D. Page layout"], answer: "B", marks: 1 },
          { id: 7, question: "Print Preview shows:", options: ["A. How the document will look when printed", "B. Print history", "C. Printer settings only", "D. Paper cost"], answer: "A", marks: 1 },
          { id: 8, question: "To undo the last action, press:", options: ["A. Ctrl + Y", "B. Ctrl + Z", "C. Ctrl + U", "D. Ctrl + D"], answer: "B", marks: 1 },
          { id: 9, question: "Spell check helps to:", options: ["A. Count words", "B. Find and correct spelling errors", "C. Change font", "D. Print documents"], answer: "B", marks: 1 },
          { id: 10, question: "The Home tab contains:", options: ["A. Print options only", "B. Basic formatting tools", "C. Insert menu only", "D. Page layout only"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 12,
        questions: [
          { id: 11, question: "List FOUR formatting features available in word processing software.", marks: 4, answer: "Bold, Italic, Underline, Font size, Font color, Text alignment, Bullets/numbering, Line spacing (any 4)" },
          { id: 12, question: "Explain the difference between Save and Save As.", marks: 4, answer: "Save: Updates the existing file with changes, keeps same name and location. Save As: Creates a new copy with option to change filename, location, or file format." },
          { id: 13, question: "Name FOUR types of documents you can create with word processing software.", marks: 4, answer: "Letters, Reports, Essays, Resumes, Invitations, Newsletters, Memos, Brochures (any 4)" }
        ]
      },
      {
        name: "Section C: Practical Task",
        marks: 18,
        questions: [
          { id: 14, question: "Create a formal letter with the following requirements: (a) Your school address as header (2 marks), (b) Today's date right-aligned (2 marks), (c) Recipient address (2 marks), (d) Subject line in bold (2 marks), (e) Three paragraphs of body text (6 marks), (f) Closing with your name (2 marks), (g) Proper formatting throughout (2 marks)", marks: 18, answer: "Practical assessment - check for proper letter format, correct alignment, appropriate formatting, spelling/grammar." }
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 5: Spreadsheets I
  // ==========================================
  unit5: {
    id: "y11ict-unit5-test",
    title: "Unit 5 Test: Spreadsheets I",
    duration: "45 minutes",
    totalMarks: 40,
    instructions: [
      "Answer ALL questions",
      "Section C requires practical demonstration"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 10,
        questions: [
          { id: 1, question: "A cell is identified by its:", options: ["A. Color", "B. Column letter and row number", "C. Size", "D. Content only"], answer: "B", marks: 1 },
          { id: 2, question: "In cell reference B5, B represents:", options: ["A. Row", "B. Column", "C. Cell value", "D. Formula"], answer: "B", marks: 1 },
          { id: 3, question: "Formulas in Excel start with:", options: ["A. + sign", "B. @ symbol", "C. = sign", "D. # symbol"], answer: "C", marks: 1 },
          { id: 4, question: "=SUM(A1:A10) will:", options: ["A. Average the values", "B. Add all values from A1 to A10", "C. Count the cells", "D. Find the maximum"], answer: "B", marks: 1 },
          { id: 5, question: "To find the average, use the function:", options: ["A. =SUM", "B. =COUNT", "C. =AVERAGE", "D. =MAX"], answer: "C", marks: 1 },
          { id: 6, question: "Which chart type is best for showing parts of a whole?", options: ["A. Bar chart", "B. Line chart", "C. Pie chart", "D. Scatter plot"], answer: "C", marks: 1 },
          { id: 7, question: "Sorting data arranges it in:", options: ["A. Random order", "B. Alphabetical or numerical order", "C. Reverse order only", "D. Original order"], answer: "B", marks: 1 },
          { id: 8, question: "A spreadsheet workbook contains:", options: ["A. Only one sheet", "B. Multiple worksheets", "C. Only formulas", "D. Only text"], answer: "B", marks: 1 },
          { id: 9, question: "The formula =B2*C2 will:", options: ["A. Add B2 and C2", "B. Multiply B2 by C2", "C. Divide B2 by C2", "D. Subtract C2 from B2"], answer: "B", marks: 1 },
          { id: 10, question: "Filtering data allows you to:", options: ["A. Delete unwanted data", "B. Display only data meeting certain criteria", "C. Change data format", "D. Copy data"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 12,
        questions: [
          { id: 11, question: "Write the formula to: (a) Add cells A1 and B1 (b) Find the maximum value in cells C1 to C10 (c) Count how many cells contain numbers in D1:D20", marks: 6, answer: "(a) =A1+B1 or =SUM(A1,B1) (b) =MAX(C1:C10) (c) =COUNT(D1:D20)" },
          { id: 12, question: "Explain the difference between a label and a value in a spreadsheet.", marks: 3, answer: "Label: Text entry used for headings or descriptions (e.g., 'Student Name'). Value: Numeric data that can be used in calculations (e.g., 85, 100)." },
          { id: 13, question: "Name THREE types of charts and when each would be appropriate.", marks: 3, answer: "Pie chart - showing parts of whole (percentages). Bar/Column chart - comparing values across categories. Line chart - showing trends over time." }
        ]
      },
      {
        name: "Section C: Practical Task",
        marks: 18,
        questions: [
          { id: 14, question: "Create a spreadsheet for a class of 5 students with: (a) Column headers: Name, Quiz1, Quiz2, Quiz3, Total, Average (3 marks), (b) Enter sample data for 5 students (3 marks), (c) Use SUM formula to calculate Total (4 marks), (d) Use AVERAGE formula to calculate Average (4 marks), (e) Create a bar chart showing student totals (4 marks)", marks: 18, answer: "Practical assessment - check for correct formulas, proper chart, formatting." }
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 6: Databases I
  // ==========================================
  unit6: {
    id: "y11ict-unit6-test",
    title: "Unit 6 Test: Databases I",
    duration: "45 minutes",
    totalMarks: 40,
    instructions: [
      "Answer ALL questions",
      "Write your answers clearly"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "A database is used to:", options: ["A. Play music", "B. Store and organize data", "C. Create presentations", "D. Browse internet"], answer: "B", marks: 1 },
          { id: 2, question: "A row in a database table is called a:", options: ["A. Field", "B. Record", "C. Column", "D. Cell"], answer: "B", marks: 1 },
          { id: 3, question: "A column in a database table is called a:", options: ["A. Record", "B. Row", "C. Field", "D. Table"], answer: "C", marks: 1 },
          { id: 4, question: "Which data type would be used for a phone number?", options: ["A. Number", "B. Text", "C. Date", "D. Currency"], answer: "B", marks: 1 },
          { id: 5, question: "A primary key is:", options: ["A. The first field", "B. A unique identifier for each record", "C. A password", "D. The largest value"], answer: "B", marks: 1 },
          { id: 6, question: "A query is used to:", options: ["A. Enter data", "B. Search for specific data", "C. Print tables", "D. Delete databases"], answer: "B", marks: 1 },
          { id: 7, question: "Which data type stores TRUE or FALSE values?", options: ["A. Text", "B. Number", "C. Yes/No (Boolean)", "D. Date"], answer: "C", marks: 1 },
          { id: 8, question: "A database form is used to:", options: ["A. Print reports", "B. Enter and view data easily", "C. Create queries", "D. Delete records"], answer: "B", marks: 1 },
          { id: 9, question: "A report is used to:", options: ["A. Enter new data", "B. Present data in a formatted printable layout", "C. Search for records", "D. Change field types"], answer: "B", marks: 1 },
          { id: 10, question: "Field size determines:", options: ["A. The maximum data that can be stored", "B. The field color", "C. The field position", "D. The field name length"], answer: "A", marks: 1 },
          { id: 11, question: "To find all customers from 'Port Vila', you would use:", options: ["A. A form", "B. A query with criteria", "C. A report only", "D. The delete function"], answer: "B", marks: 1 },
          { id: 12, question: "Data validation ensures:", options: ["A. Data is colorful", "B. Data entered meets specific rules", "C. Data is deleted", "D. Data is printed"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 15,
        questions: [
          { id: 13, question: "A school wants to create a database for student records. List FIVE fields that should be included and the appropriate data type for each.", marks: 5, answer: "StudentID (Number/AutoNumber), FirstName (Text), LastName (Text), DateOfBirth (Date), YearLevel (Number), PhoneNumber (Text), Address (Text), Gender (Text)" },
          { id: 14, question: "Explain the difference between a table, a query, and a report.", marks: 6, answer: "Table: Stores the actual data in rows (records) and columns (fields). Query: Asks questions of the data to find specific information based on criteria. Report: Presents data in a formatted, printable layout for sharing or documentation." },
          { id: 15, question: "What is data integrity and why is it important in a database?", marks: 4, answer: "Data integrity means data is accurate, consistent, and reliable. Important because: decisions are based on this data, errors can cause problems, maintains trust in the system, prevents duplicate or incorrect records." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 13,
        questions: [
          { id: 16, question: "A local store wants to track their products. Design a database table structure that includes: (a) At least 6 appropriate fields with data types (b) A suitable primary key (c) An example query they might use (d) Why this database would be more useful than a paper-based system", marks: 13, answer: "Fields: ProductID (AutoNumber, Primary Key), ProductName (Text), Category (Text), Price (Currency), StockQuantity (Number), SupplierName (Text), ReorderLevel (Number). Query example: Find all products where StockQuantity < ReorderLevel. Benefits: Quick searching, automatic calculations, no duplicate entries, can handle large amounts of data, easy to update, create reports instantly." }
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 7: Programming I
  // ==========================================
  unit7: {
    id: "y11ict-unit7-test",
    title: "Unit 7 Test: Programming I",
    duration: "45 minutes",
    totalMarks: 40,
    instructions: [
      "Answer ALL questions",
      "Show your working for algorithm questions"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 10,
        questions: [
          { id: 1, question: "An algorithm is:", options: ["A. A type of computer", "B. A step-by-step procedure to solve a problem", "C. A programming language", "D. A computer virus"], answer: "B", marks: 1 },
          { id: 2, question: "Pseudocode is:", options: ["A. Actual program code", "B. Plain language description of an algorithm", "C. A programming language", "D. Computer hardware"], answer: "B", marks: 1 },
          { id: 3, question: "A flowchart uses:", options: ["A. Only text", "B. Shapes and symbols", "C. Only numbers", "D. Pictures only"], answer: "B", marks: 1 },
          { id: 4, question: "A variable is used to:", options: ["A. Store data temporarily", "B. Print output", "C. Connect to internet", "D. Delete files"], answer: "A", marks: 1 },
          { id: 5, question: "In a flowchart, a diamond shape represents:", options: ["A. Start/End", "B. Process", "C. Decision", "D. Input/Output"], answer: "C", marks: 1 },
          { id: 6, question: "A syntax error is:", options: ["A. A logical mistake", "B. A violation of programming language rules", "C. A hardware problem", "D. An internet error"], answer: "B", marks: 1 },
          { id: 7, question: "A loop is used to:", options: ["A. End a program", "B. Repeat instructions", "C. Store data", "D. Print once"], answer: "B", marks: 1 },
          { id: 8, question: "An IF statement is an example of:", options: ["A. A loop", "B. A variable", "C. A selection/decision structure", "D. An output"], answer: "C", marks: 1 },
          { id: 9, question: "Debugging means:", options: ["A. Adding new features", "B. Finding and fixing errors", "C. Writing documentation", "D. Designing interfaces"], answer: "B", marks: 1 },
          { id: 10, question: "Which is a valid variable name?", options: ["A. 1stNumber", "B. student-name", "C. studentAge", "D. class size"], answer: "C", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 12,
        questions: [
          { id: 11, question: "List the FIVE main stages of program development.", marks: 5, answer: "1. Analysis (understand the problem), 2. Design (plan the solution), 3. Coding (write the program), 4. Testing (check for errors), 5. Maintenance (update and fix)" },
          { id: 12, question: "Draw the flowchart symbols for: Start/End, Process, Decision, Input/Output", marks: 4, answer: "Start/End: Oval/Rounded rectangle, Process: Rectangle, Decision: Diamond, Input/Output: Parallelogram" },
          { id: 13, question: "What is the difference between a syntax error and a logic error?", marks: 3, answer: "Syntax error: Violation of programming language rules, prevents program from running (e.g., missing semicolon). Logic error: Program runs but produces wrong results due to incorrect logic (e.g., wrong formula)." }
        ]
      },
      {
        name: "Section C: Coding Tasks",
        marks: 18,
        questions: [
          { id: 14, question: "Write pseudocode for a program that: (a) Asks the user to enter two numbers (b) Calculates and displays their sum, difference, and product", marks: 8, answer: "START\nINPUT num1\nINPUT num2\nsum = num1 + num2\ndifference = num1 - num2\nproduct = num1 * num2\nOUTPUT 'Sum:', sum\nOUTPUT 'Difference:', difference\nOUTPUT 'Product:', product\nEND" },
          { id: 15, question: "Write pseudocode for a program that determines if a student passed or failed based on their score (pass mark is 50).", marks: 10, answer: "START\nINPUT score\nIF score >= 50 THEN\n  OUTPUT 'Pass'\nELSE\n  OUTPUT 'Fail'\nENDIF\nEND" }
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 13: Networks I
  // ==========================================
  unit13: {
    id: "y11ict-unit13-test",
    title: "Unit 13 Test: Networks I",
    duration: "40 minutes",
    totalMarks: 35,
    instructions: [
      "Answer ALL questions",
      "Write your answers clearly"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 12,
        questions: [
          { id: 1, question: "A computer network is:", options: ["A. A single computer", "B. Two or more computers connected together", "C. A type of software", "D. A storage device"], answer: "B", marks: 1 },
          { id: 2, question: "LAN stands for:", options: ["A. Large Area Network", "B. Local Area Network", "C. Long Access Network", "D. Limited Area Network"], answer: "B", marks: 1 },
          { id: 3, question: "WAN stands for:", options: ["A. Wide Area Network", "B. Wireless Access Network", "C. Web Area Network", "D. Working Access Network"], answer: "A", marks: 1 },
          { id: 4, question: "TCP/IP is a:", options: ["A. Hardware device", "B. Communication protocol", "C. Type of cable", "D. Storage medium"], answer: "B", marks: 1 },
          { id: 5, question: "A router is used to:", options: ["A. Store files", "B. Connect different networks", "C. Print documents", "D. Create documents"], answer: "B", marks: 1 },
          { id: 6, question: "A switch connects devices in:", options: ["A. Different countries", "B. The same network", "C. Outer space", "D. Different buildings only"], answer: "B", marks: 1 },
          { id: 7, question: "Star topology has:", options: ["A. All devices in a line", "B. All devices connected to a central hub", "C. All devices in a circle", "D. Random connections"], answer: "B", marks: 1 },
          { id: 8, question: "Bus topology uses:", options: ["A. A central hub", "B. A single cable with devices attached", "C. Circular connections", "D. Wireless only"], answer: "B", marks: 1 },
          { id: 9, question: "An IP address identifies:", options: ["A. A printer model", "B. A device on a network", "C. A file type", "D. A user's name"], answer: "B", marks: 1 },
          { id: 10, question: "Ethernet is a type of:", options: ["A. Wireless technology", "B. Wired network technology", "C. Programming language", "D. Operating system"], answer: "B", marks: 1 },
          { id: 11, question: "A modem:", options: ["A. Stores data", "B. Converts digital to analog signals and vice versa", "C. Prints documents", "D. Creates graphics"], answer: "B", marks: 1 },
          { id: 12, question: "WiFi uses:", options: ["A. Cables only", "B. Radio waves", "C. Fiber optics only", "D. Telephone lines only"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 12,
        questions: [
          { id: 13, question: "List FOUR advantages of using a computer network.", marks: 4, answer: "Resource sharing (printers, files), Communication (email, messaging), Data sharing and centralized backup, Cost savings on hardware, Centralized management (any 4)" },
          { id: 14, question: "Describe THREE different network topologies and draw a simple diagram for each.", marks: 6, answer: "Star: All devices connect to central hub - reliable, easy to add devices. Bus: Single cable, all devices connect to it - simple, but failure affects all. Ring: Devices connected in circle - data flows one direction, failure breaks ring." },
          { id: 15, question: "Explain the role of TCP/IP in computer networking.", marks: 2, answer: "TCP/IP is a set of protocols that govern how data is transmitted over networks. TCP breaks data into packets and ensures reliable delivery; IP handles addressing and routing packets to correct destination." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 11,
        questions: [
          { id: 16, question: "Your school wants to set up a computer network for a new computer lab with 20 computers. (a) Recommend a suitable topology and explain why. (b) List the hardware devices needed. (c) Explain TWO benefits this network will provide for students and teachers.", marks: 11, answer: "(a) Star topology - reliable (one computer failure doesn't affect others), easy to add new computers, easier to troubleshoot. (b) 20 computers, 1 switch (24+ ports), 1 router, network cables, possibly a server, wireless access point optional. (c) Benefits: Students can share files and resources, teachers can distribute/collect work electronically, central storage for educational materials, internet access for research." }
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 14: Web & Internet
  // ==========================================
  unit14: {
    id: "y11ict-unit14-test",
    title: "Unit 14 Test: Web & Internet",
    duration: "45 minutes",
    totalMarks: 40,
    instructions: [
      "Answer ALL questions",
      "Section C requires practical demonstration"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 10,
        questions: [
          { id: 1, question: "HTML stands for:", options: ["A. Hyper Text Making Language", "B. Hyper Text Markup Language", "C. High Text Modern Language", "D. Hyper Transfer Markup Language"], answer: "B", marks: 1 },
          { id: 2, question: "A web browser is used to:", options: ["A. Create websites", "B. View websites", "C. Host websites", "D. Delete websites"], answer: "B", marks: 1 },
          { id: 3, question: "URL stands for:", options: ["A. Universal Resource Locator", "B. Uniform Resource Locator", "C. United Resource Link", "D. Universal Remote Link"], answer: "B", marks: 1 },
          { id: 4, question: "Which is a search engine?", options: ["A. Microsoft Word", "B. Google", "C. Windows", "D. USB"], answer: "B", marks: 1 },
          { id: 5, question: "The WWW stands for:", options: ["A. World Wide Web", "B. World Wireless Web", "C. Wide World Web", "D. Web Wide World"], answer: "A", marks: 1 },
          { id: 6, question: "HTML tags are enclosed in:", options: ["A. Parentheses ( )", "B. Angle brackets < >", "C. Square brackets [ ]", "D. Curly braces { }"], answer: "B", marks: 1 },
          { id: 7, question: "A hyperlink is used to:", options: ["A. Make text bold", "B. Navigate to another page or resource", "C. Delete content", "D. Print pages"], answer: "B", marks: 1 },
          { id: 8, question: "<h1> creates a:", options: ["A. Paragraph", "B. Main heading", "C. Horizontal line", "D. Link"], answer: "B", marks: 1 },
          { id: 9, question: "<p> creates a:", options: ["A. Picture", "B. Paragraph", "C. Page break", "D. Password"], answer: "B", marks: 1 },
          { id: 10, question: "To add an image in HTML, use:", options: ["A. <picture>", "B. <img>", "C. <image>", "D. <photo>"], answer: "B", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 12,
        questions: [
          { id: 11, question: "Explain the difference between a web browser and a search engine.", marks: 4, answer: "Web browser: Software application used to access and view websites (e.g., Chrome, Firefox, Safari). Search engine: A website that helps find information on the internet by searching through indexed pages (e.g., Google, Bing, Yahoo)." },
          { id: 12, question: "List FOUR popular web browsers.", marks: 2, answer: "Google Chrome, Mozilla Firefox, Microsoft Edge, Safari, Opera, Brave (any 4)" },
          { id: 13, question: "Write the HTML code to create: (a) A main heading with text 'Welcome' (b) A paragraph with text 'This is my website' (c) A link to www.google.com", marks: 6, answer: "(a) <h1>Welcome</h1> (b) <p>This is my website</p> (c) <a href='www.google.com'>Google</a>" }
        ]
      },
      {
        name: "Section C: Practical Task",
        marks: 18,
        questions: [
          { id: 14, question: "Create a simple HTML webpage about your school that includes: (a) A suitable title in the browser tab (2 marks), (b) A main heading with your school name (2 marks), (c) At least 2 paragraphs about your school (4 marks), (d) A subheading (2 marks), (e) An unordered list of 3 school subjects (4 marks), (f) A hyperlink to an educational website (4 marks)", marks: 18, answer: "Practical assessment - check for proper HTML structure, correct tags, working hyperlink." }
        ]
      }
    ]
  },

  // ==========================================
  // UNIT 15: Email Communication
  // ==========================================
  unit15: {
    id: "y11ict-unit15-test",
    title: "Unit 15 Test: Email Communication",
    duration: "35 minutes",
    totalMarks: 30,
    instructions: [
      "Answer ALL questions",
      "Write your answers clearly"
    ],
    sections: [
      {
        name: "Section A: Multiple Choice",
        marks: 10,
        questions: [
          { id: 1, question: "Email stands for:", options: ["A. Electronic mail", "B. Extra mail", "C. Express mail", "D. Easy mail"], answer: "A", marks: 1 },
          { id: 2, question: "The @ symbol in an email address separates:", options: ["A. First and last name", "B. Username and domain", "C. Subject and message", "D. Sender and receiver"], answer: "B", marks: 1 },
          { id: 3, question: "CC in email means:", options: ["A. Cancel Copy", "B. Carbon Copy", "C. Clear Cache", "D. Create Contact"], answer: "B", marks: 1 },
          { id: 4, question: "BCC stands for:", options: ["A. Blind Carbon Copy", "B. Basic Carbon Copy", "C. Back Carbon Copy", "D. Best Carbon Copy"], answer: "A", marks: 1 },
          { id: 5, question: "An email attachment is:", options: ["A. A new email address", "B. A file sent with the email", "C. The subject line", "D. The signature"], answer: "B", marks: 1 },
          { id: 6, question: "SPAM is:", options: ["A. Important email", "B. Unwanted junk email", "C. A file type", "D. An email provider"], answer: "B", marks: 1 },
          { id: 7, question: "Which is a public email provider?", options: ["A. Microsoft Word", "B. Gmail", "C. Excel", "D. Windows"], answer: "B", marks: 1 },
          { id: 8, question: "The subject line should:", options: ["A. Be left empty", "B. Briefly describe the email content", "C. Contain the full message", "D. Include passwords"], answer: "B", marks: 1 },
          { id: 9, question: "Phishing emails try to:", options: ["A. Help you", "B. Steal personal information", "C. Speed up your computer", "D. Organize files"], answer: "B", marks: 1 },
          { id: 10, question: "To reply to all recipients, use:", options: ["A. Reply", "B. Forward", "C. Reply All", "D. Delete"], answer: "C", marks: 1 }
        ]
      },
      {
        name: "Section B: Short Answer",
        marks: 12,
        questions: [
          { id: 11, question: "List the FIVE main components of an email message.", marks: 5, answer: "To (recipient), From (sender), Subject, Body/Message, Attachments (optional), CC/BCC (optional), Signature (optional)" },
          { id: 12, question: "Explain THREE security measures to protect yourself when using email.", marks: 3, answer: "Don't open attachments from unknown senders, Don't click suspicious links, Use strong passwords, Be cautious of phishing attempts, Keep antivirus updated, Don't share personal information via email" },
          { id: 13, question: "What is the difference between CC and BCC? When would you use each?", marks: 4, answer: "CC (Carbon Copy): All recipients can see who else received the email. Use when you want everyone to know who was copied. BCC (Blind Carbon Copy): Recipients cannot see who else received the email. Use for privacy or mass emails where recipients shouldn't see others' addresses." }
        ]
      },
      {
        name: "Section C: Extended Response",
        marks: 8,
        questions: [
          { id: 14, question: "Write a formal email to your teacher asking for help with a homework assignment. Include: appropriate greeting, clear explanation of your problem, polite request for help, and proper closing. Explain what makes this email professional.", marks: 8, answer: "Example: Subject: Request for help with ICT Assignment. Dear Mr./Mrs. [Teacher], I hope this email finds you well. I am writing to request assistance with the database assignment due next week. I am having difficulty understanding how to create queries with multiple criteria. Would it be possible to arrange a time to meet and discuss this? I am available after school on Tuesday or Wednesday. Thank you for your time. Kind regards, [Student Name]. Professional elements: Clear subject, formal greeting, specific request, polite tone, proper closing." }
        ]
      }
    ]
  }
};

// ============================================
// UNIT EXERCISES
// ============================================
export const year11ICTExercises = {
  // UNIT 1 Exercises
  unit1: {
    unitTitle: "Unit 1: Introduction to Computer Systems",
    exercises: [
      {
        id: "ex1-1",
        title: "Exercise 1.1: Hardware Identification",
        type: "labeling",
        instructions: "Label all the hardware components shown in the diagram.",
        tasks: [
          "Identify input devices (keyboard, mouse, scanner)",
          "Identify output devices (monitor, printer, speakers)",
          "Identify storage devices (hard drive, USB, CD/DVD)",
          "Identify processing components (CPU, motherboard, RAM)"
        ],
        materials: "Computer diagram worksheet"
      },
      {
        id: "ex1-2",
        title: "Exercise 1.2: Desktop Navigation",
        type: "practical",
        instructions: "Practice navigating the desktop environment.",
        tasks: [
          "Open and close applications using different methods",
          "Minimize, maximize, and resize windows",
          "Use the Start menu to find programs",
          "Create a shortcut on the desktop",
          "Arrange icons on the desktop"
        ],
        materials: "Computer with Windows/Mac OS"
      },
      {
        id: "ex1-3",
        title: "Exercise 1.3: File Management",
        type: "practical",
        instructions: "Practice file and folder operations.",
        tasks: [
          "Create a new folder",
          "Rename a file and folder",
          "Move files between folders",
          "Copy and paste files",
          "Delete and restore files from Recycle Bin",
          "Search for files using different criteria"
        ],
        materials: "Computer with sample files"
      }
    ]
  },

  // UNIT 4 Exercises
  unit4: {
    unitTitle: "Unit 4: Word Processing I",
    exercises: [
      {
        id: "ex4-1",
        title: "Exercise 4.1: Basic Document Creation",
        type: "practical",
        instructions: "Create and format a simple document.",
        tasks: [
          "Type a short paragraph about your favorite hobby",
          "Apply bold to headings",
          "Apply italic to important words",
          "Change the font to Arial, size 12",
          "Center the title",
          "Save the document with an appropriate name"
        ],
        materials: "Word processor software"
      },
      {
        id: "ex4-2",
        title: "Exercise 4.2: Formal Letter Writing",
        type: "practical",
        instructions: "Create a formal letter using proper formatting.",
        tasks: [
          "Set up proper margins (1 inch)",
          "Add sender's address at top",
          "Include date (right-aligned)",
          "Add recipient's address",
          "Write subject line (bold)",
          "Create body with 3 paragraphs",
          "Add closing and signature"
        ],
        materials: "Word processor, letter template guide"
      },
      {
        id: "ex4-3",
        title: "Exercise 4.3: Editing and Proofreading",
        type: "practical",
        instructions: "Edit a document with intentional errors.",
        tasks: [
          "Find and fix spelling errors using spell check",
          "Correct grammatical mistakes",
          "Replace repeated words with synonyms using Find & Replace",
          "Adjust paragraph spacing",
          "Add page numbers",
          "Create a header with your name"
        ],
        materials: "Document with errors (provided by teacher)"
      }
    ]
  },

  // UNIT 5 Exercises
  unit5: {
    unitTitle: "Unit 5: Spreadsheets I",
    exercises: [
      {
        id: "ex5-1",
        title: "Exercise 5.1: Basic Spreadsheet Creation",
        type: "practical",
        instructions: "Create a spreadsheet for tracking weekly expenses.",
        tasks: [
          "Enter column headers (Day, Food, Transport, Entertainment, Total)",
          "Enter sample data for 7 days",
          "Use SUM formula for daily totals",
          "Use SUM formula for category totals",
          "Format cells as currency",
          "Add borders and shading"
        ],
        materials: "Spreadsheet software"
      },
      {
        id: "ex5-2",
        title: "Exercise 5.2: Using Functions",
        type: "practical",
        instructions: "Practice using spreadsheet functions.",
        tasks: [
          "Create a grade sheet with 10 students and 5 subjects",
          "Use SUM to calculate total marks",
          "Use AVERAGE to find mean scores",
          "Use MAX and MIN to find highest/lowest scores",
          "Use COUNT to count entries",
          "Use IF function to determine Pass/Fail"
        ],
        materials: "Spreadsheet software, sample data"
      },
      {
        id: "ex5-3",
        title: "Exercise 5.3: Creating Charts",
        type: "practical",
        instructions: "Create different types of charts from data.",
        tasks: [
          "Create a column chart comparing monthly sales",
          "Create a pie chart showing market share",
          "Create a line chart showing temperature changes",
          "Add titles and labels to all charts",
          "Customize colors and formatting",
          "Move charts to appropriate locations"
        ],
        materials: "Spreadsheet with sample data"
      }
    ]
  },

  // UNIT 7 Exercises
  unit7: {
    unitTitle: "Unit 7: Programming I",
    exercises: [
      {
        id: "ex7-1",
        title: "Exercise 7.1: Algorithm Design",
        type: "written",
        instructions: "Write algorithms for everyday tasks.",
        tasks: [
          "Write an algorithm for making a sandwich",
          "Write an algorithm for getting ready for school",
          "Write an algorithm for borrowing a library book",
          "Identify input, process, and output for each"
        ],
        materials: "Paper and pen"
      },
      {
        id: "ex7-2",
        title: "Exercise 7.2: Flowchart Creation",
        type: "drawing",
        instructions: "Draw flowcharts for the following problems.",
        tasks: [
          "Flowchart to find if a number is positive or negative",
          "Flowchart to calculate the area of a rectangle",
          "Flowchart to find the largest of three numbers",
          "Flowchart for a simple login system"
        ],
        materials: "Graph paper, flowchart symbol guide"
      },
      {
        id: "ex7-3",
        title: "Exercise 7.3: Pseudocode Writing",
        type: "written",
        instructions: "Write pseudocode solutions.",
        tasks: [
          "Program to convert temperature from Celsius to Fahrenheit",
          "Program to calculate simple interest",
          "Program to find if a number is even or odd",
          "Program to calculate the factorial of a number"
        ],
        materials: "Paper, pseudocode conventions guide"
      }
    ]
  },

  // UNIT 14 Exercises
  unit14: {
    unitTitle: "Unit 14: Web & Internet",
    exercises: [
      {
        id: "ex14-1",
        title: "Exercise 14.1: Web Search Skills",
        type: "research",
        instructions: "Practice effective web searching.",
        tasks: [
          "Find information about Vanuatu's history using different keywords",
          "Use quotation marks for exact phrase searches",
          "Use site: operator to search within a specific website",
          "Evaluate website credibility (who wrote it, when, sources)",
          "Compare information from multiple sources"
        ],
        materials: "Computer with internet access"
      },
      {
        id: "ex14-2",
        title: "Exercise 14.2: Basic HTML Page",
        type: "practical",
        instructions: "Create a personal webpage using HTML.",
        tasks: [
          "Create basic HTML structure (html, head, body tags)",
          "Add a title to the page",
          "Create headings (h1, h2, h3)",
          "Add paragraphs of text",
          "Create an ordered and unordered list",
          "Add at least one hyperlink"
        ],
        materials: "Text editor (Notepad), web browser"
      },
      {
        id: "ex14-3",
        title: "Exercise 14.3: Multi-page Website",
        type: "practical",
        instructions: "Create a simple 3-page website.",
        tasks: [
          "Create index.html (home page)",
          "Create about.html (about page)",
          "Create contact.html (contact page)",
          "Link all pages together using navigation",
          "Add consistent styling to all pages",
          "Test all links work correctly"
        ],
        materials: "Text editor, web browser"
      }
    ]
  }
};

// ============================================
// RESOURCES
// ============================================
export const year11ICTResources = [
  {
    id: "res-1",
    title: "Computer Hardware Guide",
    type: "Reference Sheet",
    unit: "Unit 1",
    description: "Visual guide to PC components with descriptions and functions"
  },
  {
    id: "res-2",
    title: "Keyboard Shortcut Cheat Sheet",
    type: "Quick Reference",
    unit: "Unit 4",
    description: "Common keyboard shortcuts for Windows and word processing"
  },
  {
    id: "res-3",
    title: "Spreadsheet Formula Guide",
    type: "Reference Sheet",
    unit: "Unit 5",
    description: "Common spreadsheet functions with syntax and examples"
  },
  {
    id: "res-4",
    title: "Database Design Template",
    type: "Template",
    unit: "Unit 6",
    description: "Template for planning database structure and fields"
  },
  {
    id: "res-5",
    title: "Flowchart Symbol Guide",
    type: "Reference Sheet",
    unit: "Unit 7",
    description: "Standard flowchart symbols and their meanings"
  },
  {
    id: "res-6",
    title: "HTML Quick Reference",
    type: "Cheat Sheet",
    unit: "Unit 14",
    description: "Common HTML tags and their usage"
  },
  {
    id: "res-7",
    title: "Email Etiquette Guide",
    type: "Guidelines",
    unit: "Unit 15",
    description: "Best practices for professional email communication"
  },
  {
    id: "res-8",
    title: "Network Topology Diagrams",
    type: "Visual Aid",
    unit: "Unit 13",
    description: "Diagrams showing different network topologies"
  },
  {
    id: "res-9",
    title: "Computer History Timeline",
    type: "Infographic",
    unit: "Unit 2",
    description: "Timeline of major computing milestones"
  },
  {
    id: "res-10",
    title: "Data Security Checklist",
    type: "Checklist",
    unit: "Unit 3",
    description: "Security best practices for protecting data"
  },
  {
    id: "res-11",
    title: "DTP Layout Templates",
    type: "Templates",
    unit: "Unit 9",
    description: "Sample layouts for newsletters, brochures, and posters"
  },
  {
    id: "res-12",
    title: "Presentation Design Tips",
    type: "Guidelines",
    unit: "Unit 12",
    description: "Best practices for creating effective presentations"
  }
];

export default {
  year11ICTTestBlueprints,
  year11ICTUnitTests,
  year11ICTExercises,
  year11ICTResources
};
